import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { loadRecordsByEquality } from './firestoreQueryHelpers';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function normalizeNumber(value, fallback = 0) {
  if (value === '' || value === null || typeof value === 'undefined') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeArray(values = []) {
  if (Array.isArray(values)) {
    return values.map((value) => normalizeString(value)).filter(Boolean);
  }

  return normalizeString(values)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

function normalizeVisibility(value = 'private') {
  const normalized = normalizeString(value || 'private').toLowerCase();
  if (['public', 'organization', 'private'].includes(normalized)) return normalized;
  return 'private';
}

function toCurrencyCode(value = 'USD') {
  return normalizeString(value || 'USD').toUpperCase() || 'USD';
}

export function normalizeService(record = {}, fallbackId = '') {
  const id = record.id || record.serviceId || fallbackId || '';
  const packageEligible = Boolean(record.packageEligible ?? record.availableForPackages);

  return {
    id,
    serviceId: id,
    organizationId: record.organizationId || '',
    instructorId: record.instructorId || '',
    name: normalizeString(record.name || ''),
    description: normalizeString(record.description || ''),
    shortDescription: normalizeString(record.shortDescription || ''),
    discipline: normalizeString(record.discipline || ''),
    serviceType: normalizeString(record.serviceType || ''),
    durationMinutes: normalizeNumber(record.durationMinutes, 60),
    price: normalizeNumber(record.price, 0),
    currency: toCurrencyCode(record.currency || 'USD'),
    active: record.active !== false,
    visibility: normalizeVisibility(record.visibility || 'private'),
    maxParticipants: normalizeNumber(record.maxParticipants, 1),
    locationType: normalizeString(record.locationType || 'flexible'),
    locationLabel: normalizeString(record.locationLabel || ''),
    availableForPackages: packageEligible,
    packageEligible,
    applicablePackageIds: normalizeArray(record.applicablePackageIds || []),
    bookingMode: normalizeString(record.bookingMode || 'request'),
    sortOrder: normalizeNumber(record.sortOrder, 0),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export function canViewPublicService(service = {}) {
  const normalized = normalizeService(service);
  return normalized.active && normalized.visibility === 'public';
}

export function isServiceEligibleForPackage(service = {}, membershipOrPackage = {}) {
  const normalizedService = normalizeService(service);
  if (!normalizedService.packageEligible) return false;

  const packageId = membershipOrPackage.packageId || membershipOrPackage.servicePackageId || '';
  if (!packageId) return true;
  if ((normalizedService.applicablePackageIds || []).length === 0) return true;
  return normalizedService.applicablePackageIds.includes(packageId);
}

export function getServicePricingSummary(
  service = {},
  {
    memberships = [],
  } = {}
) {
  const normalized = normalizeService(service);
  const eligibleMembershipCount = (memberships || []).filter((membership) =>
    isServiceEligibleForPackage(normalized, membership)
  ).length;

  return {
    serviceId: normalized.serviceId,
    price: normalized.price,
    currency: normalized.currency,
    durationMinutes: normalized.durationMinutes,
    packageEligible: normalized.packageEligible,
    eligibleMembershipCount,
    paymentHint: normalized.price > 0 ? `${normalized.price} ${normalized.currency}` : 'Custom pricing',
  };
}

export async function createService(
  db,
  {
    organizationId = '',
    instructorId = '',
    service = {},
  } = {}
) {
  const normalized = normalizeService({
    ...service,
    organizationId,
    instructorId: instructorId || service.instructorId || '',
  });

  const docRef = (await addDoc(collection(db, PLATFORM_COLLECTIONS.SERVICES), {
    ...normalized,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })) || {};

  return {
    ...normalized,
    id: docRef.id || normalized.id || '',
    serviceId: docRef.id || normalized.serviceId || '',
  };
}

export async function updateService(db, serviceId = '', updates = {}) {
  if (!serviceId) throw new Error('Service id is required.');
  const normalized = normalizeService(updates, serviceId);
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.SERVICES, serviceId), {
    ...normalized,
    updatedAt: serverTimestamp(),
  });
  return normalized;
}

export async function deactivateService(db, serviceId = '') {
  if (!serviceId) throw new Error('Service id is required.');
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.SERVICES, serviceId), {
    active: false,
    updatedAt: serverTimestamp(),
  });
  return { serviceId, active: false };
}

export async function getServiceById(db, serviceId = '') {
  if (!serviceId) return null;
  const snapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.SERVICES, serviceId));
  if (!snapshot.exists()) return null;
  return normalizeService({ id: snapshot.id, ...snapshot.data() });
}

export async function getOrganizationServices(db, organizationId = '', { activeOnly = false } = {}) {
  if (!organizationId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.SERVICES,
    field: 'organizationId',
    value: organizationId,
  });

  return records
    .map((record) => normalizeService(record))
    .filter((service) => (activeOnly ? service.active : true));
}

export async function getInstructorServices(db, instructorId = '', { activeOnly = false } = {}) {
  if (!instructorId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.SERVICES,
    field: 'instructorId',
    value: instructorId,
  });

  return records
    .map((record) => normalizeService(record))
    .filter((service) => (activeOnly ? service.active : true));
}

async function queryPublicServices(db, filters = []) {
  try {
    const constraints = [
      where('active', '==', true),
      where('visibility', '==', 'public'),
      ...filters.map((filter) => where(filter.field, filter.operator || '==', filter.value)),
    ];
    const snapshot = await getDocs(query(collection(db, PLATFORM_COLLECTIONS.SERVICES), ...constraints));
    return snapshot.docs.map((docSnap) => normalizeService({ id: docSnap.id, ...docSnap.data() }));
  } catch (error) {
    console.warn('Public services query failed:', error);
    return [];
  }
}

export async function getPublicOrganizationServices(db, organizationId = '') {
  if (!organizationId) return [];
  const records = await queryPublicServices(db, [{ field: 'organizationId', value: organizationId }]);
  return records.filter(canViewPublicService);
}

export async function getPublicInstructorServices(db, instructorId = '') {
  if (!instructorId) return [];
  const records = await queryPublicServices(db, [{ field: 'instructorId', value: instructorId }]);
  return records.filter(canViewPublicService);
}

export async function getBookableServices(
  db,
  {
    organizationId = '',
    instructorId = '',
    publicOnly = false,
    activeOnly = true,
  } = {}
) {
  const records = instructorId
    ? await getInstructorServices(db, instructorId, { activeOnly })
    : await getOrganizationServices(db, organizationId, { activeOnly });

  return (records || []).filter((service) => {
    if (publicOnly && service.visibility !== 'public') return false;
    return service.active && service.bookingMode === 'request';
  });
}

export async function searchPublicServices(db, filters = {}) {
  const records = await queryPublicServices(db, []);
  return records.filter((service) => {
    if (!canViewPublicService(service)) return false;
    if (filters.organizationId && service.organizationId !== filters.organizationId) return false;
    if (filters.instructorId && service.instructorId !== filters.instructorId) return false;
    if (filters.discipline && service.discipline !== filters.discipline) return false;
    if (filters.serviceType && service.serviceType !== filters.serviceType) return false;
    if (filters.locationType && service.locationType !== filters.locationType) return false;
    if (filters.packageEligible && !service.packageEligible) return false;
    if (filters.minPrice !== undefined && filters.minPrice !== '' && service.price < Number(filters.minPrice)) return false;
    if (filters.maxPrice !== undefined && filters.maxPrice !== '' && service.price > Number(filters.maxPrice)) return false;
    if (filters.durationMinutes && service.durationMinutes !== Number(filters.durationMinutes)) return false;
    if (filters.searchTerm) {
      const haystack = [
        service.name,
        service.shortDescription,
        service.description,
        service.discipline,
        service.serviceType,
        service.locationLabel,
      ].join(' ').toLowerCase();
      if (!haystack.includes(String(filters.searchTerm).toLowerCase())) return false;
    }
    return true;
  });
}

export async function getFeaturedServices(db, filters = {}) {
  const results = await searchPublicServices(db, filters);
  return results
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .slice(0, 6);
}

export function getDiscoverableServiceFacets(services = []) {
  const normalized = (services || []).map((service) => normalizeService(service));
  return {
    disciplines: Array.from(new Set(normalized.map((service) => service.discipline).filter(Boolean))).sort(),
    serviceTypes: Array.from(new Set(normalized.map((service) => service.serviceType).filter(Boolean))).sort(),
    locationTypes: Array.from(new Set(normalized.map((service) => service.locationType).filter(Boolean))).sort(),
    durations: Array.from(new Set(normalized.map((service) => service.durationMinutes).filter(Boolean))).sort((a, b) => a - b),
  };
}
